import type { Unsubscribe } from 'firebase/firestore'
import { AuthenticationService } from '@core/services/authentication/authentication-service'
import {
  createActivityComment,
  createCommentReply,
  deleteActivityComment,
  deleteActivityCommentReply,
  fetchActivityComment,
  fetchActivityCommentReply,
  fetchActivityExploreComments,
  fetchInstagramReelComments,
  fetchWebinarSuggestionComments,
  fetchNumberOfPodcastComments,
  fetchNumberOfInstagramReelComments,
  fetchNumberOfWebinarSuggestionComments,
  fetchNumberOfWebinarComments,
  fetchNumberOfSymptomComments,
  fetchPodcastComments,
  fetchWebinarComments,
  fetchSymptomComments,
  fetchNumberOfActivityExploreComments,
  likeComment,
  likeCommentReply,
  observeActivityComment,
  observeActivityExploreComments,
  observeNumberOfPodcastComments,
  observeNumberOfInstagramReelComments,
  observeNumberOfWebinarSuggestionComments,
  observeNumberOfWebinarComments,
  observeNumberOfSymptomComments,
  observePodcastComments,
  observeInstagramReelComments,
  observeWebinarSuggestionComments,
  observeWebinarComments,
  observeSymptomComments,
  observeCommentReplies,
  observeNumberOfActivityExploreComments,
  type ObserveActivityExploreCommentsOptions,
  type ObserveCommentRepliesOptions,
  unlikeComment,
  unlikeCommentReply,
  updateActivityComment,
  updateActivityCommentReply,
} from '@core/services/activity-comments/activity-comments-service'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type {
  ActivityComment,
  CommentReply,
} from '@domain/models/activity-comments'

export class ActivityCommentsFeature {
  private static getAuthUserIdOrThrow(): string {
    const userId = AuthenticationService.getInstance().userId
    if (!userId) {
      throw new CoreError({
        cause: CoreErrorCause.Forbidden,
        context: 'No authenticated user available for activity comments feature.',
      })
    }
    return userId
  }

  public static async getActivityExploreComments(
    activityExploreId: string,
    options?: ObserveActivityExploreCommentsOptions,
  ): Promise<ActivityComment[]> {
    try {
      return await fetchActivityExploreComments(activityExploreId, options)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch activity explore comments.',
        underlyingException: error,
      })
    }
  }

  public static async getNumberOfActivityExploreComments(
    activityExploreId: string,
  ): Promise<number> {
    try {
      return await fetchNumberOfActivityExploreComments(activityExploreId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch number of activity explore comments.',
        underlyingException: error,
      })
    }
  }

  public static async getWebinarComments(
    webinarId: string,
    options?: ObserveActivityExploreCommentsOptions,
  ): Promise<ActivityComment[]> {
    try {
      return await fetchWebinarComments(webinarId, options)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch webinar comments.',
        underlyingException: error,
      })
    }
  }

  public static async getPodcastComments(
    podcastEpisodeId: string,
    options?: ObserveActivityExploreCommentsOptions,
  ): Promise<ActivityComment[]> {
    try {
      return await fetchPodcastComments(podcastEpisodeId, options)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch podcast comments.',
        underlyingException: error,
      })
    }
  }

  public static async getInstagramReelComments(
    reelId: string,
    options?: ObserveActivityExploreCommentsOptions,
  ): Promise<ActivityComment[]> {
    try {
      return await fetchInstagramReelComments(reelId, options)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch Instagram reel comments.',
        underlyingException: error,
      })
    }
  }

  public static async getWebinarSuggestionComments(
    suggestionId: string,
    options?: ObserveActivityExploreCommentsOptions,
  ): Promise<ActivityComment[]> {
    try {
      return await fetchWebinarSuggestionComments(suggestionId, options)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch webinar suggestion comments.',
        underlyingException: error,
      })
    }
  }

  public static async getSymptomComments(
    symptomId: string,
    whatWorksId: string,
    options?: ObserveActivityExploreCommentsOptions,
  ): Promise<ActivityComment[]> {
    try {
      return await fetchSymptomComments(symptomId, whatWorksId, options)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch symptom comments.',
        underlyingException: error,
      })
    }
  }

  public static async getNumberOfWebinarComments(
    webinarId: string,
  ): Promise<number> {
    try {
      return await fetchNumberOfWebinarComments(webinarId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch number of webinar comments.',
        underlyingException: error,
      })
    }
  }

  public static async getNumberOfPodcastComments(
    podcastEpisodeId: string,
  ): Promise<number> {
    try {
      return await fetchNumberOfPodcastComments(podcastEpisodeId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch number of podcast comments.',
        underlyingException: error,
      })
    }
  }

  public static async getNumberOfInstagramReelComments(
    reelId: string,
  ): Promise<number> {
    try {
      return await fetchNumberOfInstagramReelComments(reelId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch number of Instagram reel comments.',
        underlyingException: error,
      })
    }
  }

  public static async getNumberOfWebinarSuggestionComments(
    suggestionId: string,
  ): Promise<number> {
    try {
      return await fetchNumberOfWebinarSuggestionComments(suggestionId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch number of webinar suggestion comments.',
        underlyingException: error,
      })
    }
  }

  public static async getNumberOfSymptomComments(
    symptomId: string,
    whatWorksId: string,
  ): Promise<number> {
    try {
      return await fetchNumberOfSymptomComments(symptomId, whatWorksId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch number of symptom comments.',
        underlyingException: error,
      })
    }
  }

  public static observeActivityExploreComments(
    activityExploreId: string,
    callback: (comments: ActivityComment[]) => void,
    onError?: (error: Error) => void,
    options?: ObserveActivityExploreCommentsOptions,
  ): Unsubscribe {
    try {
      return observeActivityExploreComments(activityExploreId, callback, onError, options)
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
              cause: CoreErrorCause.Unknown,
              context: 'Failed to observe activity explore comments.',
              underlyingException: error,
            }),
      )
      return () => {}
    }
  }

  public static observeNumberOfActivityExploreComments(
    activityExploreId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    try {
      return observeNumberOfActivityExploreComments(activityExploreId, callback, onError)
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
              cause: CoreErrorCause.Unknown,
              context: 'Failed to observe number of activity explore comments.',
              underlyingException: error,
            }),
      )
      return () => {}
    }
  }

  public static observeWebinarComments(
    webinarId: string,
    callback: (comments: ActivityComment[]) => void,
    onError?: (error: Error) => void,
    options?: ObserveActivityExploreCommentsOptions,
  ): Unsubscribe {
    try {
      return observeWebinarComments(webinarId, callback, onError, options)
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
              cause: CoreErrorCause.Unknown,
              context: 'Failed to observe webinar comments.',
              underlyingException: error,
            }),
      )
      return () => {}
    }
  }

  public static observePodcastComments(
    podcastEpisodeId: string,
    callback: (comments: ActivityComment[]) => void,
    onError?: (error: Error) => void,
    options?: ObserveActivityExploreCommentsOptions,
  ): Unsubscribe {
    try {
      return observePodcastComments(podcastEpisodeId, callback, onError, options)
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
              cause: CoreErrorCause.Unknown,
              context: 'Failed to observe podcast comments.',
              underlyingException: error,
            }),
      )
      return () => {}
    }
  }

  public static observeInstagramReelComments(
    reelId: string,
    callback: (comments: ActivityComment[]) => void,
    onError?: (error: Error) => void,
    options?: ObserveActivityExploreCommentsOptions,
  ): Unsubscribe {
    try {
      return observeInstagramReelComments(reelId, callback, onError, options)
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
              cause: CoreErrorCause.Unknown,
              context: 'Failed to observe Instagram reel comments.',
              underlyingException: error,
            }),
      )
      return () => {}
    }
  }

  public static observeWebinarSuggestionComments(
    suggestionId: string,
    callback: (comments: ActivityComment[]) => void,
    onError?: (error: Error) => void,
    options?: ObserveActivityExploreCommentsOptions,
  ): Unsubscribe {
    try {
      return observeWebinarSuggestionComments(
        suggestionId,
        callback,
        onError,
        options,
      )
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
              cause: CoreErrorCause.Unknown,
              context: 'Failed to observe webinar suggestion comments.',
              underlyingException: error,
            }),
      )
      return () => {}
    }
  }

  public static observeSymptomComments(
    symptomId: string,
    whatWorksId: string,
    callback: (comments: ActivityComment[]) => void,
    onError?: (error: Error) => void,
    options?: ObserveActivityExploreCommentsOptions,
  ): Unsubscribe {
    try {
      return observeSymptomComments(
        symptomId,
        whatWorksId,
        callback,
        onError,
        options,
      )
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
              cause: CoreErrorCause.Unknown,
              context: 'Failed to observe symptom comments.',
              underlyingException: error,
            }),
      )
      return () => {}
    }
  }

  public static observeNumberOfWebinarComments(
    webinarId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    try {
      return observeNumberOfWebinarComments(webinarId, callback, onError)
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
              cause: CoreErrorCause.Unknown,
              context: 'Failed to observe number of webinar comments.',
              underlyingException: error,
            }),
      )
      return () => {}
    }
  }

  public static observeNumberOfPodcastComments(
    podcastEpisodeId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    try {
      return observeNumberOfPodcastComments(podcastEpisodeId, callback, onError)
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
              cause: CoreErrorCause.Unknown,
              context: 'Failed to observe number of podcast comments.',
              underlyingException: error,
            }),
      )
      return () => {}
    }
  }

  public static observeNumberOfInstagramReelComments(
    reelId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    try {
      return observeNumberOfInstagramReelComments(reelId, callback, onError)
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
              cause: CoreErrorCause.Unknown,
              context: 'Failed to observe number of Instagram reel comments.',
              underlyingException: error,
            }),
      )
      return () => {}
    }
  }

  public static observeNumberOfWebinarSuggestionComments(
    suggestionId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    try {
      return observeNumberOfWebinarSuggestionComments(
        suggestionId,
        callback,
        onError,
      )
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
              cause: CoreErrorCause.Unknown,
              context: 'Failed to observe number of webinar suggestion comments.',
              underlyingException: error,
            }),
      )
      return () => {}
    }
  }

  public static observeNumberOfSymptomComments(
    symptomId: string,
    whatWorksId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    try {
      return observeNumberOfSymptomComments(
        symptomId,
        whatWorksId,
        callback,
        onError,
      )
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
              cause: CoreErrorCause.Unknown,
              context: 'Failed to observe number of symptom comments.',
              underlyingException: error,
            }),
      )
      return () => {}
    }
  }

  public static async createActivityExploreComment(
    activityExploreId: string,
    comment: string,
  ): Promise<string> {
    try {
      const userId = ActivityCommentsFeature.getAuthUserIdOrThrow()
      return await createActivityComment({
        comment,
        postedBy: userId,
        activityRef: activityExploreId,
        postedOn: new Date(),
        likes: 0,
        likedBy: [],
      })
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to create activity explore comment.',
        underlyingException: error,
      })
    }
  }

  public static async createWebinarComment(
    webinarId: string,
    comment: string,
  ): Promise<string> {
    try {
      const userId = ActivityCommentsFeature.getAuthUserIdOrThrow()
      return await createActivityComment({
        comment,
        postedBy: userId,
        activityRef: webinarId,
        activityRefCollection: 'webinars',
        postedOn: new Date(),
        likes: 0,
        likedBy: [],
      })
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to create webinar comment.',
        underlyingException: error,
      })
    }
  }

  public static async createPodcastComment(
    podcastEpisodeId: string,
    comment: string,
  ): Promise<string> {
    try {
      const userId = ActivityCommentsFeature.getAuthUserIdOrThrow()
      return await createActivityComment({
        comment,
        postedBy: userId,
        activityRef: podcastEpisodeId,
        activityRefCollection: 'podcast_episodes',
        postedOn: new Date(),
        likes: 0,
        likedBy: [],
      })
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to create podcast comment.',
        underlyingException: error,
      })
    }
  }

  public static async createInstagramReelComment(
    reelId: string,
    comment: string,
  ): Promise<string> {
    try {
      const userId = ActivityCommentsFeature.getAuthUserIdOrThrow()
      return await createActivityComment({
        comment,
        postedBy: userId,
        activityRef: reelId,
        activityRefCollection: 'instagram_reels',
        postedOn: new Date(),
        likes: 0,
        likedBy: [],
      })
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to create Instagram reel comment.',
        underlyingException: error,
      })
    }
  }

  public static async createWebinarSuggestionComment(
    suggestionId: string,
    comment: string,
  ): Promise<string> {
    try {
      const userId = ActivityCommentsFeature.getAuthUserIdOrThrow()
      return await createActivityComment({
        comment,
        postedBy: userId,
        activityRef: suggestionId,
        activityRefCollection: 'webinar_suggestions',
        postedOn: new Date(),
        likes: 0,
        likedBy: [],
      })
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to create webinar suggestion comment.',
        underlyingException: error,
      })
    }
  }

  public static async createSymptomComment(
    symptomId: string,
    whatWorksId: string,
    comment: string,
  ): Promise<string> {
    try {
      const userId = ActivityCommentsFeature.getAuthUserIdOrThrow()
      return await createActivityComment({
        comment,
        postedBy: userId,
        pdSymptomId: symptomId,
        solutionRef: whatWorksId,
        postedOn: new Date(),
        likes: 0,
        likedBy: [],
      })
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to create symptom comment.',
        underlyingException: error,
      })
    }
  }

  public static async getActivityComment(commentId: string): Promise<ActivityComment> {
    try {
      return await fetchActivityComment(commentId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch activity comment.',
        underlyingException: error,
      })
    }
  }

  public static observeActivityComment(
    commentId: string,
    callback: (comment: ActivityComment | null) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    try {
      return observeActivityComment(commentId, callback, onError)
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
              cause: CoreErrorCause.Unknown,
              context: `Failed to observe activity comment "${commentId}".`,
              underlyingException: error,
            }),
      )
      return () => {}
    }
  }

  public static async updateComment(
    commentId: string,
    comment: string,
  ): Promise<void> {
    try {
      ActivityCommentsFeature.getAuthUserIdOrThrow()
      await updateActivityComment(commentId, comment)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to update comment "${commentId}".`,
        underlyingException: error,
      })
    }
  }

  public static async deleteComment(commentId: string): Promise<void> {
    try {
      ActivityCommentsFeature.getAuthUserIdOrThrow()
      await deleteActivityComment(commentId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to delete comment "${commentId}".`,
        underlyingException: error,
      })
    }
  }

  public static async toggleLikeComment(comment: ActivityComment): Promise<boolean> {
    try {
      const userId = ActivityCommentsFeature.getAuthUserIdOrThrow()
      const isLiked = Boolean(comment.likedBy?.includes(userId))
      if (isLiked) {
        await unlikeComment(comment.uid, userId)
        return false
      }
      await likeComment(comment.uid, userId)
      return true
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to toggle like for comment "${comment.uid}".`,
        underlyingException: error,
      })
    }
  }

  public static async getCommentReply(
    commentId: string,
    replyId: string,
  ): Promise<CommentReply> {
    try {
      return await fetchActivityCommentReply(commentId, replyId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch comment reply.',
        underlyingException: error,
      })
    }
  }

  public static observeCommentReplies(
    commentId: string,
    callback: (replies: CommentReply[]) => void,
    onError?: (error: Error) => void,
    options?: ObserveCommentRepliesOptions,
  ): Unsubscribe {
    try {
      return observeCommentReplies(commentId, callback, onError, options)
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
              cause: CoreErrorCause.Unknown,
              context: `Failed to observe replies for comment "${commentId}".`,
              underlyingException: error,
            }),
      )
      return () => {}
    }
  }

  public static async createReply(
    commentId: string,
    comment: string,
  ): Promise<string> {
    try {
      const userId = ActivityCommentsFeature.getAuthUserIdOrThrow()
      return await createCommentReply(commentId, {
        comment,
        postedBy: userId,
        postedOn: new Date(),
        likes: 0,
        likedBy: [],
      })
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to create reply for comment "${commentId}".`,
        underlyingException: error,
      })
    }
  }

  public static async updateReply(
    commentId: string,
    replyId: string,
    comment: string,
  ): Promise<void> {
    try {
      ActivityCommentsFeature.getAuthUserIdOrThrow()
      await updateActivityCommentReply(commentId, replyId, comment)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to update reply "${replyId}".`,
        underlyingException: error,
      })
    }
  }

  public static async deleteReply(commentId: string, replyId: string): Promise<void> {
    try {
      ActivityCommentsFeature.getAuthUserIdOrThrow()
      await deleteActivityCommentReply(commentId, replyId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to delete reply "${replyId}".`,
        underlyingException: error,
      })
    }
  }

  public static async toggleLikeReply(
    commentId: string,
    reply: CommentReply,
  ): Promise<boolean> {
    try {
      const userId = ActivityCommentsFeature.getAuthUserIdOrThrow()
      const isLiked = Boolean(reply.likedBy?.includes(userId))
      if (isLiked) {
        await unlikeCommentReply(commentId, reply.uid, userId)
        return false
      }
      await likeCommentReply(commentId, reply.uid, userId)
      return true
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to toggle like for reply "${reply.uid}".`,
        underlyingException: error,
      })
    }
  }
}

export async function getActivityExploreComments(
  activityExploreId: string,
  options?: ObserveActivityExploreCommentsOptions,
): Promise<ActivityComment[]> {
  return ActivityCommentsFeature.getActivityExploreComments(activityExploreId, options)
}

export async function getNumberOfActivityExploreComments(
  activityExploreId: string,
): Promise<number> {
  return ActivityCommentsFeature.getNumberOfActivityExploreComments(activityExploreId)
}

export async function getWebinarComments(
  webinarId: string,
  options?: ObserveActivityExploreCommentsOptions,
): Promise<ActivityComment[]> {
  return ActivityCommentsFeature.getWebinarComments(webinarId, options)
}

export async function getPodcastComments(
  podcastEpisodeId: string,
  options?: ObserveActivityExploreCommentsOptions,
): Promise<ActivityComment[]> {
  return ActivityCommentsFeature.getPodcastComments(podcastEpisodeId, options)
}

export async function getInstagramReelComments(
  reelId: string,
  options?: ObserveActivityExploreCommentsOptions,
): Promise<ActivityComment[]> {
  return ActivityCommentsFeature.getInstagramReelComments(reelId, options)
}

export async function getWebinarSuggestionComments(
  suggestionId: string,
  options?: ObserveActivityExploreCommentsOptions,
): Promise<ActivityComment[]> {
  return ActivityCommentsFeature.getWebinarSuggestionComments(suggestionId, options)
}

export async function getSymptomComments(
  symptomId: string,
  whatWorksId: string,
  options?: ObserveActivityExploreCommentsOptions,
): Promise<ActivityComment[]> {
  return ActivityCommentsFeature.getSymptomComments(symptomId, whatWorksId, options)
}

export async function getNumberOfWebinarComments(
  webinarId: string,
): Promise<number> {
  return ActivityCommentsFeature.getNumberOfWebinarComments(webinarId)
}

export async function getNumberOfPodcastComments(
  podcastEpisodeId: string,
): Promise<number> {
  return ActivityCommentsFeature.getNumberOfPodcastComments(podcastEpisodeId)
}

export async function getNumberOfInstagramReelComments(
  reelId: string,
): Promise<number> {
  return ActivityCommentsFeature.getNumberOfInstagramReelComments(reelId)
}

export async function getNumberOfWebinarSuggestionComments(
  suggestionId: string,
): Promise<number> {
  return ActivityCommentsFeature.getNumberOfWebinarSuggestionComments(suggestionId)
}

export async function getNumberOfSymptomComments(
  symptomId: string,
  whatWorksId: string,
): Promise<number> {
  return ActivityCommentsFeature.getNumberOfSymptomComments(symptomId, whatWorksId)
}

export function observeActivityExploreCommentsLive(
  activityExploreId: string,
  callback: (comments: ActivityComment[]) => void,
  onError?: (error: Error) => void,
  options?: ObserveActivityExploreCommentsOptions,
): Unsubscribe {
  return ActivityCommentsFeature.observeActivityExploreComments(
    activityExploreId,
    callback,
    onError,
    options,
  )
}

export function observeNumberOfActivityExploreCommentsLive(
  activityExploreId: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return ActivityCommentsFeature.observeNumberOfActivityExploreComments(
    activityExploreId,
    callback,
    onError,
  )
}

export function observeWebinarCommentsLive(
  webinarId: string,
  callback: (comments: ActivityComment[]) => void,
  onError?: (error: Error) => void,
  options?: ObserveActivityExploreCommentsOptions,
): Unsubscribe {
  return ActivityCommentsFeature.observeWebinarComments(
    webinarId,
    callback,
    onError,
    options,
  )
}

export function observePodcastCommentsLive(
  podcastEpisodeId: string,
  callback: (comments: ActivityComment[]) => void,
  onError?: (error: Error) => void,
  options?: ObserveActivityExploreCommentsOptions,
): Unsubscribe {
  return ActivityCommentsFeature.observePodcastComments(
    podcastEpisodeId,
    callback,
    onError,
    options,
  )
}

export function observeInstagramReelCommentsLive(
  reelId: string,
  callback: (comments: ActivityComment[]) => void,
  onError?: (error: Error) => void,
  options?: ObserveActivityExploreCommentsOptions,
): Unsubscribe {
  return ActivityCommentsFeature.observeInstagramReelComments(
    reelId,
    callback,
    onError,
    options,
  )
}

export function observeWebinarSuggestionCommentsLive(
  suggestionId: string,
  callback: (comments: ActivityComment[]) => void,
  onError?: (error: Error) => void,
  options?: ObserveActivityExploreCommentsOptions,
): Unsubscribe {
  return ActivityCommentsFeature.observeWebinarSuggestionComments(
    suggestionId,
    callback,
    onError,
    options,
  )
}

export function observeSymptomCommentsLive(
  symptomId: string,
  whatWorksId: string,
  callback: (comments: ActivityComment[]) => void,
  onError?: (error: Error) => void,
  options?: ObserveActivityExploreCommentsOptions,
): Unsubscribe {
  return ActivityCommentsFeature.observeSymptomComments(
    symptomId,
    whatWorksId,
    callback,
    onError,
    options,
  )
}

export function observeNumberOfWebinarCommentsLive(
  webinarId: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return ActivityCommentsFeature.observeNumberOfWebinarComments(
    webinarId,
    callback,
    onError,
  )
}

export function observeNumberOfPodcastCommentsLive(
  podcastEpisodeId: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return ActivityCommentsFeature.observeNumberOfPodcastComments(
    podcastEpisodeId,
    callback,
    onError,
  )
}

export function observeNumberOfInstagramReelCommentsLive(
  reelId: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return ActivityCommentsFeature.observeNumberOfInstagramReelComments(
    reelId,
    callback,
    onError,
  )
}

export function observeNumberOfWebinarSuggestionCommentsLive(
  suggestionId: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return ActivityCommentsFeature.observeNumberOfWebinarSuggestionComments(
    suggestionId,
    callback,
    onError,
  )
}

export function observeNumberOfSymptomCommentsLive(
  symptomId: string,
  whatWorksId: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return ActivityCommentsFeature.observeNumberOfSymptomComments(
    symptomId,
    whatWorksId,
    callback,
    onError,
  )
}

export async function createActivityExploreComment(
  activityExploreId: string,
  comment: string,
): Promise<string> {
  return ActivityCommentsFeature.createActivityExploreComment(activityExploreId, comment)
}

export async function createWebinarComment(
  webinarId: string,
  comment: string,
): Promise<string> {
  return ActivityCommentsFeature.createWebinarComment(webinarId, comment)
}

export async function createPodcastComment(
  podcastEpisodeId: string,
  comment: string,
): Promise<string> {
  return ActivityCommentsFeature.createPodcastComment(podcastEpisodeId, comment)
}

export async function createInstagramReelComment(
  reelId: string,
  comment: string,
): Promise<string> {
  return ActivityCommentsFeature.createInstagramReelComment(reelId, comment)
}

export async function createWebinarSuggestionComment(
  suggestionId: string,
  comment: string,
): Promise<string> {
  return ActivityCommentsFeature.createWebinarSuggestionComment(suggestionId, comment)
}

export async function createSymptomComment(
  symptomId: string,
  whatWorksId: string,
  comment: string,
): Promise<string> {
  return ActivityCommentsFeature.createSymptomComment(symptomId, whatWorksId, comment)
}

export async function getActivityComment(commentId: string): Promise<ActivityComment> {
  return ActivityCommentsFeature.getActivityComment(commentId)
}

export function observeActivityCommentLive(
  commentId: string,
  callback: (comment: ActivityComment | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return ActivityCommentsFeature.observeActivityComment(commentId, callback, onError)
}

export async function updateComment(commentId: string, comment: string): Promise<void> {
  return ActivityCommentsFeature.updateComment(commentId, comment)
}

export async function deleteComment(commentId: string): Promise<void> {
  return ActivityCommentsFeature.deleteComment(commentId)
}

export async function toggleLikeComment(comment: ActivityComment): Promise<boolean> {
  return ActivityCommentsFeature.toggleLikeComment(comment)
}

export async function getCommentReply(
  commentId: string,
  replyId: string,
): Promise<CommentReply> {
  return ActivityCommentsFeature.getCommentReply(commentId, replyId)
}

export function observeCommentRepliesLive(
  commentId: string,
  callback: (replies: CommentReply[]) => void,
  onError?: (error: Error) => void,
  options?: ObserveCommentRepliesOptions,
): Unsubscribe {
  return ActivityCommentsFeature.observeCommentReplies(commentId, callback, onError, options)
}

export async function createReply(commentId: string, comment: string): Promise<string> {
  return ActivityCommentsFeature.createReply(commentId, comment)
}

export async function updateReply(
  commentId: string,
  replyId: string,
  comment: string,
): Promise<void> {
  return ActivityCommentsFeature.updateReply(commentId, replyId, comment)
}

export async function deleteReply(commentId: string, replyId: string): Promise<void> {
  return ActivityCommentsFeature.deleteReply(commentId, replyId)
}

export async function toggleLikeReply(
  commentId: string,
  reply: CommentReply,
): Promise<boolean> {
  return ActivityCommentsFeature.toggleLikeReply(commentId, reply)
}
